import React, { useEffect, useState } from "react";
import { HandIcon } from "./HandIcon";

// PortraitLock — keeps the client mobile app in portrait.
//
// True OS-level orientation locking isn't available to a plain web app on
// every platform: the Screen Orientation API's `lock()` only works when (a)
// the browser supports it (Android Chrome; not Safari) and (b) the page is
// running fullscreen/standalone — which an installed home-screen PWA is, but
// a browser tab never is. iOS ignores both the API and the manifest's
// "orientation" key entirely, in or out of Safari.
//
// So this does both things available to us:
//   1. Best-effort `screen.orientation.lock("portrait")` for the case it DOES
//      work (installed Android PWA) — silently ignored everywhere else.
//   2. A full-screen "please rotate back" blocker for everyone else, so a
//      client who rotates lands on a clear instruction instead of the app
//      silently reflowing into whatever layout the current width happens to
//      match (which is what produced the mobile-shell-skips-the-disclosure-
//      gate bug this was added alongside).
//
// Matches on `pointer: coarse` (touch) so a desktop browser resized narrow
// never triggers it, and caps the device's larger dimension at 926px (the
// widest current phone in landscape) so tablets — which have a legitimate
// landscape use — aren't blocked.

const MAX_PHONE_DIMENSION = 926;

function isPhoneish(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (!window.matchMedia("(pointer: coarse)").matches) return false;
  } catch { /* matchMedia unavailable — assume desktop, never block */ return false; }
  return Math.max(window.screen?.width || 0, window.screen?.height || 0) <= MAX_PHONE_DIMENSION;
}

function isLandscape(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.matchMedia("(orientation: landscape)").matches;
  } catch {
    return window.innerWidth > window.innerHeight;
  }
}

export default function PortraitLock() {
  const [blocked, setBlocked] = useState(() => isPhoneish() && isLandscape());

  useEffect(() => {
    // Best-effort native lock — no-op (throws, caught) anywhere unsupported.
    try {
      const orientation: any = (window.screen as any)?.orientation;
      orientation?.lock?.("portrait")?.catch?.(() => {});
    } catch { /* Screen Orientation API unavailable */ }

    const onChange = () => setBlocked(isPhoneish() && isLandscape());
    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
    };
  }, []);

  if (!blocked) return null;

  return (
    <div data-overlay="blocker" style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "#1F2937", color: "white",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "32px", textAlign: "center", gap: "14px",
    }}>
      <div aria-hidden style={{ fontSize: "42px", transform: "rotate(90deg)" }}><HandIcon name="phone" /></div>
      <div style={{ fontSize: "17px", fontWeight: 700 }}>Please rotate back to portrait</div>
      <p style={{ margin: 0, fontSize: "13.5px", color: "#D1D5DB", maxWidth: "280px", lineHeight: 1.5 }}>
        Sourdough is designed for portrait mode on phones.
      </p>
    </div>
  );
}
